/** Public category constraints are review assistance, never Card or readiness authority. */
export const EBAY_CCG_POLICY_VERSION = 1 as const;
export const EBAY_CCG_CATEGORY_ID = '183454' as const;
export const EBAY_CATEGORY_POLICY_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const EBAY_CATEGORY_POLICY_PUBLIC_MAX_BYTES = 64 * 1024;

export type EbayPolicyEnvironment = 'PRODUCTION' | 'SANDBOX';
export interface EbayCategoryPolicyBinding {
  schemaVersion: 1;
  parserVersion: 1;
  environment: EbayPolicyEnvironment;
  marketplace: 'EBAY_US';
  locale: 'en-US';
  categoryId: '183454';
  categoryTreeId: string;
  categoryTreeVersion: string;
  semanticDigest: string;
  rawSha256: { tree: string; aspects: string; conditions: string };
  templateSha256: string;
  /** SHA256 of UTF8 JSON.stringify(the exact ordered 106 header strings), no newline. */
  templateHeadersSha256: string;
  fetchedAt: string;
  expiresAt: string;
}
export interface EbayAspectValueDependency {
  aspectName: string;
  values: string[];
}
export interface EbayCategoryAspectConstraint {
  name: string;
  dataType: 'STRING' | 'NUMBER';
  format?: 'int32';
  cardinality: 'SINGLE' | 'MULTI';
  mode: 'FREE_TEXT' | 'SELECTION_ONLY';
  required: boolean;
  usage: 'RECOMMENDED' | 'OPTIONAL';
  enabledForVariations: boolean;
  applicableTo?: Array<'PRODUCT' | 'ITEM'>;
  /** Provider maximum, absent when the provider did not supply one. */
  maxLength?: number;
  /** Complete only for SELECTION_ONLY. FREE_TEXT suggestions are never an enum. */
  selectionValues?: string[];
  suggestions: string[];
  suggestionsTruncated: boolean;
  /** Full known-value dependency lookup stays server-side. */
  hasValueDependencies: boolean;
}
export interface EbayCcgConditionPolicy {
  raw: Array<{
    condition: 'NEAR_MINT_OR_BETTER' | 'EXCELLENT' | 'VERY_GOOD' | 'POOR';
    valueId: string;
    label: string;
  }>;
  graded: Array<{
    grader: 'PSA' | 'BGS' | 'SGC' | 'CGC' | 'TAG' | 'HGA';
    valueId: string;
    grades: Array<{ grade: number; valueId: string }>;
  }>;
  certificationMaxLength: number;
  certificationRequired: boolean;
  gradedConditionId: '2750';
  rawConditionId: '4000';
}
export interface EbayCategoryPolicy {
  binding: EbayCategoryPolicyBinding;
  aspects: EbayCategoryAspectConstraint[];
  conditions: EbayCcgConditionPolicy;
  evidence: 'POINT_IN_TIME_PUBLIC_POLICY';
}

/** Refresh timestamps/raw response formatting do not silently change reviewed semantics. */
export function ebayCategoryPolicyReviewIdentity(binding: EbayCategoryPolicyBinding): string {
  return JSON.stringify([
    binding.schemaVersion,
    binding.parserVersion,
    binding.environment,
    binding.marketplace,
    binding.locale,
    binding.categoryId,
    binding.categoryTreeId,
    binding.categoryTreeVersion,
    binding.semanticDigest,
    binding.templateSha256,
    binding.templateHeadersSha256,
  ]);
}

export function isEbayCategoryPolicyFresh(
  binding: EbayCategoryPolicyBinding,
  nowMs: number,
): boolean {
  const fetched = Date.parse(binding.fetchedAt);
  const expires = Date.parse(binding.expiresAt);
  return (
    Number.isFinite(nowMs) &&
    Number.isFinite(fetched) &&
    Number.isFinite(expires) &&
    fetched <= nowMs &&
    expires > nowMs &&
    expires > fetched &&
    expires - fetched <= EBAY_CATEGORY_POLICY_MAX_AGE_MS
  );
}
